import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from '../hooks/use-toast.js';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form.js';
import { Input } from '../components/ui/input.js';
import { Textarea } from '../components/ui/textarea.js';
import { Button } from '../components/ui/button.js';

const contactFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().min(10, { message: "Please enter a valid phone number" }),
  subject: z.string().min(2, { message: "Subject must be at least 2 characters" }),
  message: z.string().min(10, { message: "Message must be at least 10 characters" }),
});

const locations = [
  {
    id: 1,
    name: "YorkGate Mall Location",
    address: "1 York Gate Blvd, North York, ON M3N 3A1, Canada",
    phone: "(647) 200-2040",
    hours: "Mon-Fri: 10am-8pm\nSat: 10am-6pm\nSun: 12pm-5pm",
  },
  {
    id: 2,
    name: "Mississauga Location",
    address: "50 Valley Blvd, Mississauga, ON L5A 3S2, Canada",
    phone: "(647) 200-2040",
    hours: "Mon-Fri: 10am-8pm\nSat: 10am-6pm\nSun: 12pm-5pm",
  }
];

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const form = useForm({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  function onSubmit(data) {
    // In a real implementation, this would submit the form data to a server
    toast({
      title: "Message Sent Successfully!",
      description: "We've received your message and will get back to you as soon as possible.",
      variant: "default",
    });
    form.reset();
    setIsSubmitted(true);
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Background Image */}
      <section 
        className="relative h-[400px] bg-cover bg-center bg-no-repeat" 
        style={{ backgroundImage: "url('/images/contact-us.jpg')" }}
      >
        <div className="absolute inset-0 bg-black/50 flex items-center">
          <div className="container mx-auto px-4 text-center">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
              <p className="text-xl md:text-2xl max-w-3xl mx-auto">
                Have questions or need help? Contact our support team or visit one of our locations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Get In Touch</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We're here to help with all your device repair and telecom needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div>
              <h2 className="text-2xl font-semibold mb-6">Send Us a Message</h2>
              
              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-8 rounded-lg">
                  <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
                  <p>Your message has been sent successfully. We'll get back to you as soon as possible.</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setIsSubmitted(false)}
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Your Name</FormLabel>
                          <FormControl>
                            <Input placeholder="John Doe" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="john.doe@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                              <Input placeholder="(123) 456-7890" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subject</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Repair inquiry" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Please type your message here..."
                              className="resize-none h-32"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <Button
                      type="submit"
                      className="bg-primary hover:bg-primary/90"
                    >
                      Send Message
                    </Button>
                  </form>
                </Form>
              )}
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold mb-6">Our Locations</h2>
              
              <div className="space-y-6">
                {locations.map((location) => (
                  <div key={location.id} className="bg-neutral p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold mb-2">{location.name}</h3>
                    <div className="mb-4">
                      <p className="flex items-start mb-2">
                        <i className="fas fa-map-marker-alt text-primary mt-1 mr-3"></i>
                        <span>{location.address}</span>
                      </p>
                      <p className="flex items-start mb-2">
                        <i className="fas fa-phone-alt text-primary mt-1 mr-3"></i>
                        <span>{location.phone}</span>
                      </p>
                      <p className="flex items-start mb-2">
                        <i className="fas fa-clock text-primary mt-1 mr-3"></i>
                        <span dangerouslySetInnerHTML={{ __html: location.hours.replace(/\n/g, '<br>') }}></span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">General Contact Information</h3>
                <div className="space-y-3">
                  <p className="flex items-center">
                    <i className="fas fa-envelope text-primary mr-3"></i>
                    <span>repair@ziwatech.ca</span>
                  </p>
                  <p className="flex items-center">
                    <i className="fas fa-phone-alt text-primary mr-3"></i>
                    <span>Customer Service: +1647-200-2040</span>
                  </p>
                  <p className="flex items-center">
                    <i className="fas fa-headset text-primary mr-3"></i>
                    <span>Technical Support: +1647-200-2040</span>
                  </p>
                </div>
                
                <div className="mt-8">
                  <h3 className="text-xl font-semibold mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a href="#" className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary hover:bg-opacity-20 transition-all">
                      <i className="fab fa-facebook-f"></i>
                    </a>
                    <a href="#" className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary hover:bg-opacity-20 transition-all">
                      <i className="fab fa-twitter"></i>
                    </a>
                    <a href="#" className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary hover:bg-opacity-20 transition-all">
                      <i className="fab fa-instagram"></i>
                    </a>
                    <a href="#" className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary hover:bg-opacity-20 transition-all">
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
